import React, { useState, useEffect } from 'react'
import Checkbox from '@mui/material/Checkbox'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import Paper from '@mui/material/Paper'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`
    };
}

function TableDefinition(props) {
    const [tableHasHeader, setTableHasHeader] = useState(false)
    const [namedColumns, setNamedColumns] = useState(false)
    const [zipFileId, setZipFileId] = useState('')
    const [textQualifier, setTextQualifier] = useState(-1)
    const [sourceFiles, setSourceFiles] = useState([])
    const [textDelimiter, setTextDelimiter] = useState(0)
    const [customDelimiter, setCustomDelimiter] = useState('')
    const [selectedExcelTables, setSelectedExcelTables] = useState([])
    const [tabData, setTabData] = useState([])
    const [externalTabData, setExternalTabData] = useState([])
    const [currentTab, setCurrentTab] = useState(0)
    const [headerRow, setHeaderRow] = useState(0)

    // trigger on component mount
    useEffect(() => {
        setZipFileId(props.zipFileId)
        setTextQualifier(props.textQualifier)
        setSourceFiles(props.sourceFiles)
        setTextDelimiter(props.textDelimiter)
        setCustomDelimiter(props.customDelimiter)
        setSelectedExcelTables(props.selectedExcelTables)
        setTableHasHeader(props.tableDefinition.tableHasHeader)
        setNamedColumns(props.tableDefinition.namedColumns)

        if (props.previewData.length > 0) {
            setTabData(props.previewData)
        }
    }, [])

    useEffect(() => {
        if (props.zipFileId !== zipFileId) {
            setZipFileId(props.zipFileId)
        }
        if (props.textQualifier !== textQualifier) {
            setTextQualifier(props.textQualifier)
        }
        if (props.sourceFiles !== null && props.sourceFiles !== undefined && !arraysAreEqual(props.sourceFiles, sourceFiles)) {
            setSourceFiles(props.sourceFiles)
        }
        if (props.textDelimiter !== textDelimiter) {
            setTextDelimiter(props.textDelimiter)
        }
        if (props.customDelimiter !== customDelimiter) {
            setCustomDelimiter(props.customDelimiter)
        }
        if (props.selectedExcelTables !== null && props.selectedExcelTables !== undefined && !arraysAreEqual(props.selectedExcelTables, selectedExcelTables)) {
            setSelectedExcelTables(props.selectedExcelTables)
        }
    }, [props.zipFileId, props.textQualifier, props.sourceFiles, props.textDelimiter, props.customDelimiter, props.selectedExcelTables])

    useEffect(() => {
        if (props.tableDefinition.tableHasHeader !== tableHasHeader) {
            setTableHasHeader(props.tableDefinition.tableHasHeader)
        }
        if (props.tableDefinition.namedColumns !== namedColumns) {
            setNamedColumns(props.tableDefinition.namedColumns)
        }
    }, [props.tableDefinition])

    /**
     * arraysAreEqual
     * * Compare two different arrays to see if they are the same
     * @param {Array} arr1 - First Array
     * @param {Array} arr2 - Second Array
    */
    function arraysAreEqual(arr1, arr2) {
        // Check if arrays have the same length
        if (arr1.length !== arr2.length) {
            return false;
        }
    
        // Check if each element in the arrays is identical
        for (let i = 0; i < arr1.length; i++) {
            if (arr1[i] !== arr2[i]) {
                return false;
            }
        }
    
        // If both conditions pass, arrays are equal
        return true;
    }

    function renderTableOrMessageStatingNothingWasSelected() {
        if (sourceFiles.length === 0) {
            return <Typography variant="body1">No Files Were Found, Cannot Render Preview Data</Typography>
        } else if (tabData.length > 0) {
            return <Tabs value={currentTab} onChange={(event, newValue) => setCurrentTab(newValue)} aria-label="Preview Data Tabs" variant="scrollable">
                {tabData.map((tab, index) => {return <Tab key={`tab-data-${index}`} label={tab["tabName"]} {...a11yProps(index)} />})}
            </Tabs>
        } else {
            props.getPreviewData(zipFileId, textQualifier, getAllIdsFromList(sourceFiles), textDelimiter, customDelimiter, selectedExcelTables).then((resp) => {
                if (resp.status !== 200) {
                    throw new Error()
                } else {
                    return resp.json()
                }
            })
            .then((json) => {
                let selectedData = []
                let externalData = []
                
                for (let x = 0; x < json.length; x++) {
                    if (json[x]["selected"] === true) {
                        selectedData.push(json[x])
                    } else {
                        externalData.push(json[x])
                    }
                }

                setTabData(selectedData)
                setExternalTabData(externalData)

                let firstIndexHeaderRow = 0
                if (selectedData.length > 0) {
                    firstIndexHeaderRow = selectedData[0]["headerRow"]
                    setHeaderRow(firstIndexHeaderRow)
                }

                fireOnSaveEvent(tableHasHeader, namedColumns, selectedData, externalData, firstIndexHeaderRow)
            }).catch((ex) => {
                console.error(ex)
            })
        }

        return <Typography variant="body1">Loading Preview Data...</Typography>
    }

    function renderSelectedPreviewDataPanel() {
        if (tabData.length > currentTab) {
            return <TableContainer component={Paper} style={{ overflowY: "none", "height":"550px" }}>
                <Table style={{ width: "100%" }} size="small" aria-label="preview data">
                    <TableHead>
                        <TableRow>
                            <TableCell></TableCell>
                            {tabData[currentTab].previewData[0].map((tabInfo, tabInfoIndex) => {
                                return <TableCell key={`row-excel-header-${tabInfoIndex}`} align="center">{props.getExcelColumnName(tabInfoIndex + 1)}</TableCell>
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tabData[currentTab].previewData.map((tabInfo, tabInfoIndex) => {
                            return <TableRow
                                key={`tab-${currentTab}-row-${tabInfoIndex}`}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                style={returnRowColorStandardRow(tabInfoIndex, tabInfo)}>
                                    <TableCell align="center">{tabInfoIndex+1}</TableCell>
                                    {renderPreviewRow(tabInfo, tabInfoIndex)}
                            </TableRow>
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        }

        return null
    }

    function renderPreviewRow(tabInfo, rowIndex) {
        return tabInfo.map((data, index) => {
            return <TableCell key={`row-${rowIndex}-cell-${index}`} align="center">AAA{data}</TableCell>
        })
    }

    function returnRowColorStandardRow(rowIndex, tabInfo) {
        // Make sure we are not suddenly dealing with an empty data set and we're on the actual row dedicated as the header
        if (tabData.length > currentTab && headerRow === rowIndex) { 
            if (tabData[currentTab].previewData.length > headerRow) {
                if (namedColumns) {
                    return { "backgroundColor": props.uiColors.ROW_HIGHLIGHT }
                } else {
                    if (tableHasHeader && props.doesThisRowHaveDataInEverySingleColumn(tabInfo)) {
                        return { "backgroundColor": props.uiColors.FACT_COLUMN_HIGHLIGHT }
                    }
                }
            }
        }

        if (tableHasHeader && props.doesThisRowHaveDataInEverySingleColumn(tabInfo)) {
            return { "backgroundColor": props.uiColors.FACT_COLUMN_HIGHLIGHT }
        }
        
        return { "backgroundColor": props.uiColors.REGULAR }
    }

    /**
     * fireOnSaveEvent
     * * If component has a prop for the [onSave] event, the purpose of this is to fire that event with the latest information
     * @param {boolean} hasHeader - Import Facts For All Members
     * @param {boolean} useNamedColumns - Import Facts For Some Members
     * @param {Object[]} previewData - Preview data with tab names
     */
    function fireOnSaveEvent(hasHeader, useNamedColumns, previewData, externalData, firstIndexHeaderRow) {
        if (props.onSave) {
            props.onSave({
                "tableHasHeader": hasHeader,
                "namedColumns": useNamedColumns,
                "tabData": previewData,
                "externalTabData": externalData,
                "headerRow": firstIndexHeaderRow
            })
        }
    }

    /**
     * getAllIdsFromList
     * * Take all elements from a JavaScript
     */
    function getAllIdsFromList(files) {
        let rtnVal = []
        for (let x = 0; x < files.length; x++) {
            rtnVal.push(files[x]["id"])
        }
        return rtnVal
    }

    return <>
        <FormGroup>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={tableHasHeader}
                        onChange={(e) => {
                            setTableHasHeader(e.target.checked)
                            fireOnSaveEvent(e.target.checked, namedColumns, tabData, externalTabData, headerRow)
                        }}
                    />
                }
                label="Table has header" />
        </FormGroup>
        <FormGroup>
            <RadioGroup
                row
                defaultValue="position-only"
                name="radio-buttons-group"
                onChange={(e) => {
                    if (e.target.value === 'position-only') {
                        setNamedColumns(false)
                        fireOnSaveEvent(tableHasHeader, false, tabData, externalTabData, headerRow)
                    } else {
                        setNamedColumns(true)
                        fireOnSaveEvent(tableHasHeader, true, tabData, externalTabData, headerRow)
                    }
                }}
            >
                <FormControlLabel value="position-only" control={<Radio checked={!namedColumns} />} label="Address columns by position only" />
                <FormControlLabel value="name-or-position" control={<Radio checked={namedColumns} />} label="Address columns by name or position" />
            </RadioGroup>
        </FormGroup>
        <Divider component="li" />
        {renderTableOrMessageStatingNothingWasSelected()}
        {renderSelectedPreviewDataPanel()}
    </>
}

export default TableDefinition